/**
 * report/tokens.js — the design tokens for both PDF reports.
 *
 * Every number here is taken from the B.I.G Report Design Specification and is expressed in
 * CSS pixels at 96 dpi, exactly as the specification states them. The document is created with
 * jsPDF's px unit and the px_scaling hotfix, so an A4 page is 794 x 1123 and a value copied
 * from the spec can be used verbatim. Font sizes are the one exception: jsPDF always sets them
 * in points, so they pass through pt() on the way to the page.
 */

/** CSS pixels to PDF points. 96 dpi to 72 dpi. */
export const pt = (px) => px * 0.75;

// ── Page geometry (spec section 1) ────────────────────────────────────────────

export const PAGE = {
  w: 794,
  h: 1123,
  margin: 48,               // content pages
  coverMargin: 56,
  topRuleH: 6,
  bodyTop: 6 + 34,          // body starts 34px below the top rule
  footerH: 40,
  get contentW() { return this.w - this.margin * 2; },
  get coverW() { return this.w - this.coverMargin * 2; },
  get bodyBottom() { return this.h - this.footerH - 16; },
};

// ── Colour (spec section 2) ───────────────────────────────────────────────────

const hex = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];

export const COLOR = {
  navy: hex('#0F1A73'),
  saffron: hex('#E8801E'),
  green: hex('#1E7B3C'),
  red: hex('#B93A2E'),
  ink: hex('#1A2233'),
  muted: hex('#6B7684'),
  line: hex('#E3E8EF'),
  border: hex('#D9DEE6'),
  panel: hex('#F3F6FA'),
  passBg: hex('#E4F3E8'),
  failBg: hex('#F9E6E3'),
  neutral: hex('#B8C0CC'),
  noteBg: hex('#FDF3E7'),
  noteInk: hex('#5A4630'),
  white: [255, 255, 255],
};

/** End-use / series palette, one colour per series in order. */
export const CHART = [
  hex('#0F1A73'), hex('#E8801E'), hex('#1E7B3C'),
  hex('#C9A227'), hex('#B93A2E'), hex('#4A7FB5'),
];

/** Sample a [stop, colour] ramp at t in 0..1. */
export function rampAt(ramp, t) {
  const x = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
  let a = ramp[0]; let b = ramp[ramp.length - 1];
  for (let i = 0; i < ramp.length - 1; i += 1) {
    if (x >= ramp[i][0] && x <= ramp[i + 1][0]) { a = ramp[i]; b = ramp[i + 1]; break; }
  }
  const span = b[0] - a[0] || 1;
  const f = (x - a[0]) / span;
  return [0, 1, 2].map((k) => Math.round(a[1][k] + (b[1][k] - a[1][k]) * f));
}

/**
 * Daylight illuminance ramp from the design specification: [stop 0..1, colour].
 * The floor maps colour their cells with the VIEWER's ramps instead (see
 * engine/daylight/colors.js), so that the PDF and the screen show the same picture; this is the
 * fallback for any legend whose map did not supply its own ramp.
 */
export const LUX_RAMP = [
  [0.00, hex('#0F1A73')],
  [0.22, hex('#1E5F9E')],
  [0.45, hex('#1E7B3C')],
  [0.68, hex('#C9C13A')],
  [0.85, hex('#E8801E')],
  [1.00, hex('#B93A2E')],
];


// ── Typography (spec section 3) ───────────────────────────────────────────────
// { f: jsPDF family id, s: size in px, ls: letter spacing in em, up: uppercase }

export const T = {
  coverTitle:   { f: 'PlexSerif500', s: 58, ls: -0.01, lh: 1.05 },
  coverKicker:  { f: 'PlexSans600', s: 11, ls: 0.20, up: true },
  coverSub:     { f: 'PlexSans400', s: 13 },
  coverMeta:    { f: 'PlexSans600', s: 10, ls: 0.10, up: true },

  pageTitle:    { f: 'PlexSerif400', s: 26 },
  pageKicker:   { f: 'PlexSans600', s: 10, ls: 0.18, up: true },
  pageSub:      { f: 'PlexSans400', s: 11.5 },
  statLabel:    { f: 'PlexSans600', s: 9.5, ls: 0.10, up: true },
  statValue:    { f: 'PlexSerif400', s: 28 },
  statValueMono: { f: 'PlexMono500', s: 14 },

  sectionTitle: { f: 'PlexSans700', s: 12, ls: 0.16, up: true },
  th:           { f: 'PlexSans700', s: 10.5, ls: 0.08, up: true },
  td:           { f: 'PlexSans400', s: 11.5 },
  tdStrong:     { f: 'PlexSans600', s: 11.5 },
  tdMono:       { f: 'PlexMono400', s: 10.5 },

  kpiValue:     { f: 'PlexSerif400', s: 36 },
  kpiValueSm:   { f: 'PlexSerif400', s: 26 },
  kpiLabel:     { f: 'PlexSans600', s: 10, ls: 0.12, up: true },
  kpiNote:      { f: 'PlexSans400', s: 10 },

  pill:         { f: 'PlexSans700', s: 9, ls: 0.08, up: true },
  verdictPill:  { f: 'PlexSans700', s: 15, ls: 0.12, up: true },
  tierTab:      { f: 'PlexSans700', s: 9, ls: 0.16, up: true },
  tierName:     { f: 'PlexSans700', s: 13 },

  footer:       { f: 'PlexSans400', s: 9.5 },
  footerStrong: { f: 'PlexSans700', s: 9.5 },
  body:         { f: 'PlexSans400', s: 11, lh: 1.65 },
  note:         { f: 'PlexSans400', s: 10.5, lh: 1.5 },
  caption:      { f: 'PlexSans400', s: 10 },
  mono:         { f: 'PlexMono400', s: 11 },
  monoSm:       { f: 'PlexMono400', s: 10 },
};

// ── Numbers ───────────────────────────────────────────────────────────────────
// Indian digit grouping throughout, matching the rest of the app.

export const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

export function fmtNum(v, decimals = 0) {
  if (!isNum(v)) return '-';
  return v.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** Currency, always with the rupee sign and a thin gap: "₹ 42,79,184". */
export function fmtRs(v, decimals = 0) {
  return isNum(v) ? `₹ ${fmtNum(v, decimals)}` : '-';
}

/** Percentage with an explicit sign, for savings that can go either way. */
export function fmtSignedPct(v, decimals = 1) {
  if (!isNum(v)) return '-';
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmtNum(Math.abs(v), decimals)}%`;
}

export function fmtPct(v, decimals = 1) {
  return isNum(v) ? `${fmtNum(v, decimals)}%` : '-';
}

/** Decimal places per quantity family, so the same quantity always prints the same way. */
export const DECIMALS = {
  energy: 0, cost: 0, carbon: 0, area: 1, uValue: 2, shgc: 2,
  ratio: 2, percent: 1, eui: 1, hours: 0, lpd: 1,
};

/**
 * The fonts are embedded and carry the full Latin-1 range plus the technical marks the reports
 * use, so text is drawn as authored. This exists only to strip anything that would still be a
 * missing glyph, and to hold the one substitution the specification asks for: no long dashes.
 */
export function sanitize(s) {
  return String(s ?? '')
    .replace(/[—–]/g, '-')          // em and en dash: the spec bans long dashes
    .replace(/[\u0000-\u001F\u007F]/g, '');   // control bytes never reach the page
}
